import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogActions, Button, Box, Typography,
  Tooltip, IconButton, FormControlLabel, Switch, Stepper, Step, StepLabel,
  Chip, CircularProgress, Divider, alpha, useTheme,
} from "@mui/material";
import {
  CalendarToday, NoteAlt, CloudUpload, Info as InfoIcon, EventNote,
  PublishedWithChanges, CheckCircle, Close, Description as FileIcon
} from "@mui/icons-material";
import { useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { createDols } from "../../../api/services/document";
import CustomDate from "../../CustomForm/CustomDate";
import CustomTextArea from "../../CustomForm/CustomTextArea";
import FileUpload from "../../CustomForm/FileUpload";

const DocumentsModal = ({ show, onHide, refetchDols }) => {
  const theme = useTheme();
  const { id: clientId } = useParams();
  const [activeStep, setActiveStep] = useState(0);

  const [formData, setFormData] = useState({
    application_date: "",
    issue_date: "",
    renewal_date: "",
    status: "SUPERSEDED",
    note_message: "",
    reported_to_chc: false,
    dols_cert: null,
  });

  const steps = ['Dates', 'Status', 'Notes', 'Upload', 'Review'];

  const handleNext = () => {
    if (activeStep === 0) {
      if (!formData.application_date || !formData.issue_date || !formData.renewal_date) {
        enqueueSnackbar("Please fill in all date fields before proceeding.", { variant: "warning" });
        return;
      }
    }
    if (activeStep === 3) {
      if (!formData.dols_cert) {
        enqueueSnackbar("Please upload the required file before proceeding.", { variant: "warning" });
        return;
      }
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "file" ? files[0] : value,
    }));
  };

  const handleToggleChange = (field) => (e) => {
    const value = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'status' && { status: value ? "CURRENT" : "SUPERSEDED" }),
    }));
  };

  const postDolsMutation = useMutation(createDols, {
    onSuccess: () => {
      enqueueSnackbar("DoLS created successfully", {
        variant: "success",
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
      refetchDols();
      onHide();
      setActiveStep(0);
      setFormData({
        application_date: "",
        issue_date: "",
        renewal_date: "",
        status: "SUPERSEDED",
        note_message: "",
        reported_to_chc: false,
        dols_cert: null,
      });
    },
    onError: () => {
      enqueueSnackbar("Failed to create DoLS", {
        variant: "error",
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      });
    },
  });

  const handleDolsSubmit = (e) => {
    e.preventDefault();
    const dolsData = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        dolsData.append(key, value);
      }
    });
    dolsData.append("client", clientId);
    postDolsMutation.mutate(dolsData);
  };

  const getFilePreview = () => {
    if (!formData.dols_cert) return null;
    return (
      <Box sx={{ 
        p: 2, 
        mt: 2, 
        display: 'flex', 
        alignItems: 'center',
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 2
          }}
        >
          <FileIcon sx={{ color: theme.palette.primary.main }} />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            {formData.dols_cert.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {(formData.dols_cert.size / 1024).toFixed(1)} KB
          </Typography>
        </Box>
        <IconButton 
          size="small" 
          onClick={() => setFormData(prev => ({ ...prev, dols_cert: null }))}
          sx={{ color: '#94a3b8' }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  const getStatusColor = (status) => {
    return status === "CURRENT" ? theme.palette.success.main : theme.palette.grey[500];
  };

  const SectionHeader = ({ icon, title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 3 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mr: 1.5
        }}
      >
        {React.cloneElement(icon, { sx: { color: theme.palette.primary.main, fontSize: 18 } })}
      </Box>
      <Typography variant="subtitle2" fontWeight={700} color="#1A1A2E">
        {title}
      </Typography>
      <Box
        sx={{
          flex: 1,
          height: 1,
          ml: 2,
          bgcolor: alpha(theme.palette.divider, 0.2),
        }}
      />
    </Box>
  );

  return (
    <Dialog
      open={show}
      onClose={onHide}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2.5,
          pb: 1.5,
          bgcolor: '#ffffff',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              bgcolor: "#EFF8FF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EventNote sx={{ color: "#2469F0", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
              Deprivation of Liberty Safeguard (DoLS)
            </Typography>
            <Typography variant="caption" sx={{ color: "#6B7280" }}>
              Create a new DoLS record
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onHide}
          size="small"
          sx={{
            color: "#9CA3AF",
            "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
          }}
        >
          <Close />
        </IconButton>
      </Box>

      {/* Stepper */}
      <Box sx={{ px: 3, pt: 3 }}>
        <Stepper 
          activeStep={activeStep} 
          sx={{
            '& .MuiStepLabel-root': {
              '& .MuiStepLabel-label': {
                fontWeight: 600,
                fontSize: '0.7rem',
                color: '#94a3b8'
              },
              '& .MuiStepLabel-label.Mui-active': {
                color: '#2469F0',
                fontWeight: 700
              },
              '& .MuiStepLabel-label.Mui-completed': {
                color: '#2469F0'
              }
            },
            '& .MuiStepIcon-root': {
              '&.Mui-active': {
                color: '#2469F0'
              },
              '&.Mui-completed': {
                color: '#2469F0'
              }
            }
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ p: 2.5 }}>
        <form onSubmit={handleDolsSubmit} encType="multipart/form-data">
          {/* Step 1 - Dates */}
          {activeStep === 0 && (
            <>
              <SectionHeader icon={<CalendarToday />} title="Dates" />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                <Box sx={{ flex: '1 1 calc(33.33% - 16px)', minWidth: '180px' }}>
                  <CustomDate 
                    label="Application Date *" 
                    name="application_date" 
                    value={formData.application_date} 
                    onChange={handleChange} 
                    fullWidth 
                    required 
                  />
                </Box>
                <Box sx={{ flex: '1 1 calc(33.33% - 16px)', minWidth: '180px' }}>
                  <CustomDate 
                    label="Issue Date *" 
                    name="issue_date" 
                    value={formData.issue_date} 
                    onChange={handleChange} 
                    fullWidth 
                    required 
                  />
                </Box>
                <Box sx={{ flex: '1 1 calc(33.33% - 16px)', minWidth: '180px' }}>
                  <CustomDate 
                    label="Renewal Date" 
                    name="renewal_date" 
                    value={formData.renewal_date} 
                    onChange={handleChange} 
                    fullWidth 
                  />
                </Box>
              </Box>
            </>
          )}

          {/* Step 2 - Status */}
          {activeStep === 1 && (
            <>
              <SectionHeader icon={<PublishedWithChanges />} title="Status & Reporting" />
              <Box sx={{ 
                p: 2.5, 
                bgcolor: alpha(theme.palette.primary.main, 0.02),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
              }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600} color="#1A1A2E">
                        Status
                      </Typography>
                      <Chip 
                        label={formData.status} 
                        size="small"
                        sx={{
                          mt: 0.5,
                          bgcolor: alpha(getStatusColor(formData.status), 0.1),
                          color: getStatusColor(formData.status),
                          fontWeight: 600,
                          fontSize: '0.65rem'
                        }}
                      />
                    </Box>
                    <FormControlLabel
                      control={<Switch checked={formData.status === "CURRENT"} onChange={handleToggleChange('status')} color="primary" />}
                      label="Current"
                    />
                  </Box>

                  <Divider sx={{ opacity: 0.3 }} />

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" fontWeight={600} color="#1A1A2E">
                        Reported to CHC
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Continuing Healthcare reporting
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={<Switch checked={formData.reported_to_chc} onChange={handleToggleChange('reported_to_chc')} color="secondary" />}
                      label={formData.reported_to_chc ? "Yes" : "No"}
                    />
                  </Box>
                </Box>
              </Box>
            </>
          )}

          {/* Step 3 - Notes */}
          {activeStep === 2 && (
            <>
              <SectionHeader icon={<NoteAlt />} title="Notes" />
              <CustomTextArea 
                label="Additional Notes" 
                name="note_message" 
                value={formData.note_message} 
                onChange={handleChange} 
                rows={6} 
                fullWidth 
                placeholder="Add any additional notes about the DoLS..."
              />
            </>
          )}

          {/* Step 4 - Upload */}
          {activeStep === 3 && (
            <>
              <SectionHeader icon={<CloudUpload />} title="Certificate Upload" />
              <FileUpload
                label="Upload DoLS Certificate"
                name="dols_cert"
                onChange={handleChange}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSize={5}
              />
              {getFilePreview()}
            </>
          )}

          {/* Step 5 - Review */}
          {activeStep === 4 && (
            <>
              <SectionHeader icon={<CheckCircle />} title="Review Details" />
              <Box sx={{ 
                p: 2.5, 
                bgcolor: alpha(theme.palette.primary.main, 0.02),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
              }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '150px' }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block' }}>
                      Application Date
                    </Typography>
                    <Typography variant="body2" fontWeight={500} color="#1A1A2E">
                      {formData.application_date || 'Not specified'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '150px' }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block' }}>
                      Issue Date
                    </Typography>
                    <Typography variant="body2" fontWeight={500} color="#1A1A2E">
                      {formData.issue_date || 'Not specified'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '150px' }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block' }}>
                      Renewal Date
                    </Typography>
                    <Typography variant="body2" fontWeight={500} color="#1A1A2E">
                      {formData.renewal_date || 'Not specified'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '150px' }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block' }}>
                      Status
                    </Typography>
                    <Chip 
                      label={formData.status} 
                      size="small"
                      sx={{
                        mt: 0.5,
                        bgcolor: alpha(getStatusColor(formData.status), 0.1),
                        color: getStatusColor(formData.status),
                        fontWeight: 600,
                        fontSize: '0.65rem'
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 100%' }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block' }}>
                      Reported to CHC
                    </Typography>
                    <Typography variant="body2" fontWeight={500} color="#1A1A2E">
                      {formData.reported_to_chc ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 100%' }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block' }}>
                      Notes
                    </Typography>
                    <Typography variant="body2" fontWeight={500} color="#1A1A2E">
                      {formData.note_message || 'None'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 100%' }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block' }}>
                      Uploaded File
                    </Typography>
                    {formData.dols_cert ? (
                      <Typography variant="body2" fontWeight={500} color="#1A1A2E">
                        {formData.dols_cert.name} ({(formData.dols_cert.size / 1024).toFixed(1)} KB)
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No file uploaded
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </form>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ 
        p: 2.5, 
        bgcolor: '#fafafa', 
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        gap: 1.5
      }}>
        <Button 
          onClick={onHide} 
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "10px",
            borderColor: "#D0D5DD",
            color: "#344054",
            "&:hover": {
              bgcolor: "#F9FAFB",
              borderColor: "#98A2B3",
            },
          }}
        >
          Cancel
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {activeStep > 0 && (
            <Button 
              onClick={handleBack}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "10px",
                color: "#64748b",
              }}
            >
              Back
            </Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button 
              onClick={handleNext} 
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                bgcolor: '#0f172a',
                px: 3,
                '&:hover': { bgcolor: '#1e293b' }
              }}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleDolsSubmit}
              variant="contained"
              disabled={postDolsMutation.isLoading}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                bgcolor: '#0f172a',
                px: 3,
                '&:hover': { bgcolor: '#1e293b' }
              }}
            >
              {postDolsMutation.isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Submit"
              )}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentsModal;